import { useEffect, useRef, type MouseEvent, type KeyboardEvent } from 'react'
import { Plus, X } from 'lucide-react'

import { Button } from '../ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from '../ui/context-menu'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { cn } from '../../lib/utils'
import { ReadOnlyConversationBadge } from './ReadOnlyConversationBadge'

export interface SidebarTabDescriptor {
  id: string
  kind: 'chat' | 'session'
  title: string
  isStreaming: boolean
  hasError: boolean
  badge?: 'question'
  readOnly?: boolean
}

interface TabStripProps {
  tabs: SidebarTabDescriptor[]
  activeTabId: string | null
  onSwitch: (id: string) => void
  onClose: (id: string) => void
  onNew: () => void
  onSelectConversations?: () => void
}

export type ChatTabCloseAction = 'close' | 'close-others' | 'close-all'

export interface ChatTabClosePlan {
  /** Conversation to focus before closing. Null keeps the current focus. */
  focusId: string | null
  closeIds: string[]
}

/**
 * Which conversation tabs a context-menu action closes. Session tabs stay
 * open. Close others focuses the tab that was right-clicked when it is not
 * already active, so the pane lands on the tab that remains.
 */
export function planChatTabClose(
  tabs: readonly Pick<SidebarTabDescriptor, 'id' | 'kind'>[],
  action: ChatTabCloseAction,
  tabId: string,
  activeTabId: string | null
): ChatTabClosePlan {
  const chatIds = tabs.filter((tab) => tab.kind === 'chat').map((tab) => tab.id)
  if (action === 'close-all') return { focusId: null, closeIds: chatIds }
  if (!chatIds.includes(tabId)) return { focusId: null, closeIds: [] }
  if (action === 'close') return { focusId: null, closeIds: [tabId] }
  return {
    focusId: activeTabId === tabId ? null : tabId,
    closeIds: chatIds.filter((id) => id !== tabId)
  }
}

function applyChatTabClose(
  plan: ChatTabClosePlan,
  onSwitch: (id: string) => void,
  onClose: (id: string) => void
): void {
  if (plan.focusId !== null) onSwitch(plan.focusId)
  for (const id of plan.closeIds) onClose(id)
}

/**
 * Two-level sidebar navigation. The top row switches between ordinary
 * conversations and the active branch's current ingest. Conversation tabs
 * render only while the conversations view is selected; the ingest title and
 * controls belong to its pane rather than another nested tab.
 */
export function TabStrip({
  tabs,
  activeTabId,
  onSwitch,
  onClose,
  onNew,
  onSelectConversations
}: TabStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const sessionTabs = tabs.filter((tab) => tab.kind === 'session')
  const conversationTabs = tabs.filter((tab) => tab.kind === 'chat')
  const currentSession = sessionTabs[0]
  const currentIngestSelected = currentSession?.id === activeTabId

  // Vertical wheel → horizontal scroll. Attached imperatively because
  // React's synthetic onWheel runs passive and can't preventDefault to
  // suppress page scroll. No-op when the strip fits without overflow so
  // the wheel keeps working over a short tab strip.
  useEffect(() => {
    const el = scrollRef.current
    if (el === null) return
    const onWheel = (event: WheelEvent): void => {
      if (event.deltaY === 0) return
      if (el.scrollWidth <= el.clientWidth) return
      event.preventDefault()
      el.scrollLeft += event.deltaY
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
    }
  }, [])

  // Drive the overlay thumb's size + position imperatively so scrolling
  // doesn't trigger a React re-render on every pixel. Updates on scroll
  // AND on resize (ResizeObserver) so opening a wider sidebar or
  // closing a tab rebalances the thumb immediately.
  useEffect(() => {
    const scrollEl = scrollRef.current
    const thumbEl = thumbRef.current
    if (scrollEl === null || thumbEl === null) return

    const update = (): void => {
      const visible = scrollEl.clientWidth
      const total = scrollEl.scrollWidth
      if (total <= visible) {
        thumbEl.style.display = 'none'
        return
      }
      thumbEl.style.display = ''
      thumbEl.style.width = `${(visible / total) * 100}%`
      thumbEl.style.left = `${(scrollEl.scrollLeft / total) * 100}%`
    }

    update()
    scrollEl.addEventListener('scroll', update, { passive: true })
    // ResizeObserver isn't available in JSDOM, so the test environment
    // skips the resize-driven update. Production browsers all have it
    // (Chromium / Firefox / Safari since ~2019).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(scrollEl)
    return () => {
      scrollEl.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [conversationTabs.length])

  return (
    <>
      {currentSession ? (
        <div
          role="tablist"
          aria-label="Sidebar views"
          className="grid h-10 grid-cols-2 border-b bg-muted/10"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!currentIngestSelected}
            onClick={() => {
              if (currentIngestSelected) onSelectConversations?.()
            }}
            className={cn(
              'relative cursor-pointer px-3 text-xs font-medium text-muted-foreground transition-colors',
              'hover:bg-accent/40 hover:text-foreground',
              'border-r border-border/60',
              !currentIngestSelected &&
                'bg-brand-soft text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary dark:bg-brand-soft/20'
            )}
          >
            Conversations
          </button>
          <button
            type="button"
            role="tab"
            aria-label="Current Ingest"
            aria-selected={currentIngestSelected}
            onClick={() => onSwitch(currentSession.id)}
            className={cn(
              'relative flex cursor-pointer items-center justify-center gap-1.5 px-3 text-xs font-medium text-muted-foreground transition-colors',
              'hover:bg-accent/40 hover:text-foreground',
              currentIngestSelected &&
                'bg-brand-soft text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary dark:bg-brand-soft/20'
            )}
          >
            <TabStatusDot tab={currentSession} />
            Current Ingest
          </button>
        </div>
      ) : null}

      {!currentIngestSelected ? (
        <Tabs
          value={activeTabId ?? ''}
          onValueChange={(value) => {
            if (value === '') return
            onSwitch(value)
          }}
          className="gap-0"
        >
          <section aria-label="Conversation tabs" className="border-b bg-muted/20">
            <div className="group/strip flex min-w-0 items-center">
              <div className="relative min-w-0 flex-1">
                <div ref={scrollRef} className="tab-strip-scroll overflow-x-auto overflow-y-hidden">
                  <TabsList
                    className={cn(
                      'h-9 w-fit min-w-0 max-w-none justify-start gap-0 rounded-none bg-transparent p-0'
                    )}
                  >
                    {conversationTabs.map((tab) => (
                      <SidebarTabTrigger
                        key={tab.id}
                        tab={tab}
                        tabs={tabs}
                        activeTabId={activeTabId}
                        onSwitch={onSwitch}
                        onClose={onClose}
                      />
                    ))}
                  </TabsList>
                </div>
                <div
                  ref={thumbRef}
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute bottom-0 h-1 rounded-full bg-muted-foreground/40',
                    'opacity-0 transition-opacity duration-150',
                    'group-hover/strip:opacity-100'
                  )}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label="New conversation tab"
                aria-pressed={activeTabId === null}
                onClick={onNew}
                title="New conversation"
                className={cn(
                  'h-9 shrink-0 gap-1 rounded-none border-l border-border/60 px-2.5 text-12 font-normal text-muted-foreground shadow-none',
                  'hover:bg-accent/40 hover:text-foreground',
                  activeTabId === null &&
                    'relative bg-brand-soft text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary dark:bg-brand-soft/20'
                )}
              >
                <Plus className="size-3.5" />
                <span>New</span>
              </Button>
            </div>
          </section>
        </Tabs>
      ) : null}
    </>
  )
}

function SidebarTabTrigger({
  tab,
  tabs,
  activeTabId,
  onSwitch,
  onClose
}: {
  tab: SidebarTabDescriptor
  tabs: readonly SidebarTabDescriptor[]
  activeTabId: string | null
  onSwitch: (id: string) => void
  onClose: (id: string) => void
}) {
  const closeOthersDisabled =
    planChatTabClose(tabs, 'close-others', tab.id, activeTabId).closeIds.length === 0
  const runClose = (action: ChatTabCloseAction): void => {
    applyChatTabClose(planChatTabClose(tabs, action, tab.id, activeTabId), onSwitch, onClose)
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TabsTrigger
          value={tab.id}
          className={cn(
            'group/tab relative h-9 min-w-0 max-w-50 flex-none gap-1.5 border-r border-border/60 px-3 py-0',
            'rounded-none text-12 font-normal',
            'shadow-none transition-colors',
            'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
            'data-[state=active]:bg-brand-soft data-[state=active]:text-foreground data-[state=active]:shadow-none',
            'dark:data-[state=active]:bg-brand-soft/20',
            'data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:bottom-0 data-[state=active]:after:h-0.5 data-[state=active]:after:bg-primary'
          )}
          title={tab.title}
        >
          <TabStatusDot tab={tab} />
          <span className="min-w-0 truncate">{tab.title}</span>
          {tab.kind === 'chat' && tab.readOnly ? <ReadOnlyConversationBadge /> : null}
          {tab.kind === 'chat' ? (
            <CloseTabAffordance
              ariaLabel={`Close ${tab.title}`}
              onClick={(event) => {
                event.stopPropagation()
                onClose(tab.id)
              }}
            />
          ) : null}
        </TabsTrigger>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={() => runClose('close')}>Close</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem disabled={closeOthersDisabled} onSelect={() => runClose('close-others')}>
          Close others
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => runClose('close-all')}>Close all</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

/**
 * Small status dot rendered to the left of the tab title. A question wins
 * for session tabs; chat tabs retain their existing error-over-streaming order.
 */
function TabStatusDot({ tab }: { tab: SidebarTabDescriptor }) {
  if (tab.badge === 'question') {
    return (
      <span
        aria-label="Pending question"
        className="size-1.5 shrink-0 animate-pulse rounded-full bg-st-warn"
      />
    )
  }
  if (tab.kind === 'session') {
    if (tab.hasError) {
      return (
        <span
          aria-label="Ingest session failed"
          className="size-1.5 shrink-0 rounded-full bg-destructive"
        />
      )
    }
    if (tab.isStreaming) {
      return (
        <span
          aria-label="Ingest session running"
          className="size-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: 'oklch(48% 0.115 205)' }}
        />
      )
    }
    return (
      <span
        aria-label="Ingest session complete"
        className="size-1.5 shrink-0 rounded-full bg-st-run"
      />
    )
  }
  if (tab.hasError) {
    return (
      <span aria-label="Run failed" className="size-1.5 shrink-0 rounded-full bg-destructive" />
    )
  }
  if (tab.isStreaming) {
    return (
      <span
        aria-label="Streaming"
        className="size-1.5 shrink-0 animate-pulse rounded-full bg-primary"
      />
    )
  }
  return null
}

/**
 * Hover-revealed close button inside a tab trigger. Rendered as a span
 * with `role="button"` rather than an actual `<button>` because the
 * TabsTrigger is already a `<button>` and nested interactive elements
 * are invalid HTML. Click handler stops propagation so the close action
 * doesn't also fire the tab switch.
 *
 * The element keeps its 14×14 footprint even when invisible (only
 * `opacity` animates) so the tab title doesn't shift left/right on
 * hover.
 */
function CloseTabAffordance({
  ariaLabel,
  onClick
}: {
  ariaLabel: string
  onClick: (event: MouseEvent | KeyboardEvent) => void
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
          onClick(event)
        }
      }}
      className={cn(
        '-mr-1.5 ml-1 inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm',
        'opacity-0 transition-opacity hover:bg-muted-foreground/15 hover:text-foreground',
        'group-hover/tab:opacity-60 group-hover/tab:hover:opacity-100',
        'group-data-[state=active]/tab:opacity-60'
      )}
    >
      <X className="size-3" />
    </span>
  )
}
